import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ScenarioKnowledgeReadPort } from '../knowledge/adapters/scenario-knowledge-port';
import type {
  KnowledgeClaim,
  KnowledgeSource,
  RegistryDocument,
  RequirementProjection,
  RequirementsDocument,
} from '../knowledge/consumer/contract';

/**
 * The SYN knowledge chain: official source → located passage → candidate claim → formed
 * ScenarioTemplate pin → requirement → 05 definition exact pin (or its absence) → S4
 * evaluation evidence (or the reason there is none) → the status that follows.
 *
 * WHY EVERY STATE IS PRINTED RATHER THAN COLOURED. The reader of this panel is deciding
 * whether a claim is supported, and "looks green" is not evidence. Each state is rendered
 * with its own name and the plane's own reason code, so a reviewer can disagree with a
 * specific statement instead of with an impression.
 *
 * WHAT IT REFUSES TO DO. It does not substitute a fixture when the host is unreachable; it
 * does not turn UNKNOWN into a blank or a zero; it does not show a predicate as a
 * requirement; and it offers no action that could be mistaken for dispatch, availability or
 * permission.
 */

type ReadState =
  | { readonly status: 'IDLE' }
  | { readonly status: 'LOADING' }
  | { readonly status: 'FAILED'; readonly message: string }
  | {
      readonly status: 'AVAILABLE';
      readonly registry: RegistryDocument;
      readonly requirements: RequirementsDocument;
    };

export function ScenarioKnowledgeChain({
  port,
  configurationError,
  compact,
  renderMatching,
}: {
  readonly port: ScenarioKnowledgeReadPort | null;
  readonly configurationError?: string;
  readonly compact?: boolean;
  readonly renderMatching?: (row: RequirementProjection) => ReactNode;
}) {
  const [state, setState] = useState<ReadState>({ status: 'IDLE' });
  const [selected, setSelected] = useState<string | null>(null);
  const sequence = useRef(0);
  useEffect(
    () => () => {
      sequence.current++;
    },
    [port],
  );

  async function read() {
    if (!port) return;
    const ticket = ++sequence.current;
    setState({ status: 'LOADING' });
    try {
      const registry = await port.registry();
      const requirements = await port.requirements();
      if (ticket !== sequence.current) return;
      setState({ status: 'AVAILABLE', registry, requirements });
      setSelected((current) => current ?? requirements.requirements[0]?.requirementId ?? null);
    } catch (failure) {
      if (ticket !== sequence.current) return;
      setState({
        status: 'FAILED',
        message: failure instanceof Error ? failure.message : 'scenario-knowledge read failed',
      });
    }
  }

  const sourcesById = useMemo(() => {
    const map = new Map<string, KnowledgeSource>();
    if (state.status === 'AVAILABLE') {
      for (const source of state.registry.sourceRegistry.sources) map.set(source.sourceId, source);
    }
    return map;
  }, [state]);

  const claimsById = useMemo(() => {
    const map = new Map<string, KnowledgeClaim>();
    if (state.status === 'AVAILABLE') {
      for (const claim of state.registry.claimCandidates.claims) map.set(claim.claimId, claim);
    }
    return map;
  }, [state]);

  if (!port) {
    return (
      <section className="scc-panel scc-knowledge" aria-labelledby="scc-knowledge-title">
        <header>
          <h2 id="scc-knowledge-title">
            合成知识链 <span>SYN Knowledge Chain</span>
          </h2>
          <p>来源、条款定位、候选断言、精确需求与能力定义的只读链。</p>
        </header>
        <div className="scc-panel-body">
          <div className="scc-notice" data-knowledge-state="HOLD">
            {configurationError
              ? configurationError
              : 'HOLD — 未启用显式本地开发读取。本页不会自动联系合成 Backend，也不会以 fixture 冒充。'}
          </div>
        </div>
      </section>
    );
  }

  const requirements = state.status === 'AVAILABLE' ? state.requirements : null;
  const registry = state.status === 'AVAILABLE' ? state.registry : null;
  const chosen = requirements?.requirements.find((row) => row.requirementId === selected) ?? null;

  return (
    <section className="scc-panel scc-knowledge" aria-labelledby="scc-knowledge-title">
      <header>
        <h2 id="scc-knowledge-title">
          合成知识链 <span>SYN Knowledge Chain</span>
        </h2>
        <p>
          官方来源元数据 → 条款定位 → 候选断言 → ScenarioTemplate 精确 pin → 需求 → 05 能力定义精确
          pin（或缺失）→ S4 评估证据（或缺失理由）。全部为合成、非运营数据。
        </p>
      </header>
      <div className="scc-panel-body">
        <button
          onClick={() => void read()}
          disabled={state.status === 'LOADING'}
          data-knowledge-read
        >
          {state.status === 'IDLE' ? '读取本机合成知识链' : '重新读取本机合成知识链'}
        </button>
        <p className="scc-knowledge__origin">
          本地 loopback · 只读 · 不写入 · 不触发任务、人员或设备
        </p>

        {state.status === 'LOADING' && (
          <div className="scc-notice" data-knowledge-state="LOADING">
            正在读取 Java 只读面…
          </div>
        )}
        {state.status === 'FAILED' && (
          <div className="scc-notice" data-knowledge-state="FAILED">
            读取失败：{state.message}。不代入任何本地 fixture，状态保持不可读。
          </div>
        )}

        {registry && requirements && (
          <>
            <div className="scc-knowledge__facts" data-knowledge-plane>
              <Fact label="读取通道" value={requirements.channel} />
              <Fact
                label="权威可消费"
                value={registry.readPlane.authoritativeConsumable ?? 'UNKNOWN'}
              />
              <Fact label="本地 mandate" value={registry.readPlane.localMandate ?? 'UNKNOWN'} />
              <Fact label="正式组合" value={registry.readPlane.formalComposition ?? 'UNKNOWN'} />
              <Fact
                label="真实作业授权"
                value={registry.readPlane.authorityForRealOperations ?? 'UNKNOWN'}
              />
              <Fact label="来源条数" value={String(registry.derived.sourceCount ?? 0)} />
              <Fact
                label="已识别 / 已核条款"
                value={`${registry.derived.sourceIdentified ?? 0} / ${registry.derived.clauseChecked ?? 0}`}
              />
              <Fact
                label="专业复核 / 当地适用批准"
                value={`${registry.derived.professionallyReviewed ?? 0} / ${registry.derived.localApplicabilityApproved ?? 0}`}
              />
              <Fact
                label="已撤回或仅历史"
                value={String(registry.derived.withdrawnOrHistoricalOnly ?? 0)}
              />
            </div>

            <h3>ScenarioTemplate 精确 pin（字节已验证 ≠ 已发布）</h3>
            <dl className="scc-knowledge__pin" data-knowledge-st-pin>
              {Object.entries(requirements.scenarioTemplateExactPin).map(([key, value]) => (
                <div key={key}>
                  <dt>{key}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
              <div>
                <dt>生命周期</dt>
                <dd data-knowledge-st-lifecycle>{requirements.scenarioTemplateLifecycle}</dd>
              </div>
              <div>
                <dt>发布状态</dt>
                <dd>{requirements.scenarioTemplatePublicationState}</dd>
              </div>
              <div>
                <dt>使用状态</dt>
                <dd>{requirements.scenarioTemplateUseState}</dd>
              </div>
            </dl>

            <h3>需求映射与评估状态（{requirements.requirementCount} 条）</h3>
            <div className="scc-knowledge__counts" data-knowledge-counts>
              {Object.entries(requirements.mappingCounts).map(([key, value]) => (
                <span key={'m-' + key} className="scc-chip">
                  映射 {key} {value}
                </span>
              ))}
              {Object.entries(requirements.evaluationCounts).map(([key, value]) => (
                <span key={'e-' + key} className="scc-chip">
                  评估 {key} {value}
                </span>
              ))}
            </div>
            <p className="scc-knowledge__origin">
              定义绑定分布（与映射状态分开计数：一行同时计入其映射状态与其绑定，两组合计不构成分区）：
            </p>
            <div className="scc-knowledge__counts" data-knowledge-binding-counts>
              {Object.entries(requirements.definitionBindingCounts).map(([key, value]) => (
                <span key={'b-' + key} className="scc-chip">
                  绑定 {key} {value}
                </span>
              ))}
            </div>

            <table className="scc-knowledge__table" data-knowledge-table>
              <thead>
                <tr>
                  <th>需求</th>
                  <th>来源</th>
                  <th>映射</th>
                  <th>定义绑定</th>
                  <th>评估</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {requirements.requirements.map((row) => (
                  <tr key={row.requirementId} data-requirement={row.requirementId}>
                    <td>
                      <strong>{row.requirementId}</strong>
                      <span className="scc-knowledge__effect">{row.effectCode}</span>
                    </td>
                    <td>{row.sourceState}</td>
                    <td>{row.mappingState}</td>
                    <td>{row.capabilityDefinitionExactRef.binding}</td>
                    <td>
                      {row.evaluationState}
                      <span className="scc-knowledge__reason">{row.evaluationReason}</span>
                    </td>
                    <td>
                      <button onClick={() => setSelected(row.requirementId)}>查看链</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {chosen && (
              <>
                <RequirementChain
                  row={chosen}
                  sourcesById={sourcesById}
                  claimsById={claimsById}
                  compact={compact === true}
                />
                {renderMatching?.(chosen)}
              </>
            )}

            {!compact && (
              <details className="scc-knowledge__sources" data-knowledge-sources>
                <summary>官方来源登记表（{registry.sourceRegistry.sourceCount} 条）</summary>
                <p>{registry.sourceRegistry.reviewLadder.meaning}</p>
                {registry.sourceRegistry.sources.map((source) => (
                  <article key={source.sourceId} data-source={source.sourceId}>
                    <h4>
                      {source.sourceId} · {source.title}
                    </h4>
                    <p>
                      {source.publisher} · {source.jurisdiction} · {source.sourceType} ·{' '}
                      {source.edition} · {source.editionStatus}
                    </p>
                    <p>
                      条款定位：{source.locator.state}
                      {source.locator.reference ? ' · ' + source.locator.reference : ''} ·{' '}
                      {source.locator.scope}
                    </p>
                    <p>用途限制：{source.limitation}</p>
                    <p>
                      版权：{source.copyrightPolicy} · 收录全文：{String(source.fullTextStored)} ·
                      授权作业：{source.authorityForRealOperations}
                    </p>
                    <p>
                      发布主体域名确认：{String(source.publisherConfirmedOnOfficialDomain)}
                      {source.publisherConfirmationNote
                        ? ' · ' + source.publisherConfirmationNote
                        : ''}
                    </p>
                    <p>
                      审查阶梯：
                      {Object.entries(source.review)
                        .map(([key, value]) => key + '=' + value)
                        .join(' · ')}
                    </p>
                    <a href={source.officialUrl} target="_blank" rel="noopener noreferrer">
                      查看官方页面 ↗
                    </a>
                    {source.urlCaptureNote && <p className="scc-warn">{source.urlCaptureNote}</p>}
                  </article>
                ))}
              </details>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function Fact({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function RequirementChain({
  row,
  sourcesById,
  claimsById,
  compact,
}: {
  readonly row: RequirementProjection;
  readonly sourcesById: ReadonlyMap<string, KnowledgeSource>;
  readonly claimsById: ReadonlyMap<string, KnowledgeClaim>;
  readonly compact: boolean;
}) {
  const ref = row.capabilityDefinitionExactRef;
  const evidence = row.evaluationEvidence;
  return (
    <section className="scc-knowledge__chain" data-knowledge-chain={row.requirementId}>
      <h3>
        {row.requirementId} · {row.effectCode}
      </h3>
      <p>{row.capabilityClassFromMember}</p>
      <p>
        成员字段完整度：已声明 {row.memberRowFieldCompleteness.declaredCount} 项 / UNKNOWN{' '}
        {row.memberRowFieldCompleteness.unknownCount} 项 · 阶段绑定 {row.stageBindingState} ·
        风险绑定 {row.hazardBindingState}
      </p>
      <p>
        UNKNOWN 条件：
        {row.memberRowFieldCompleteness.unknownFields.join('、') || '（无）'}
      </p>
      <p data-chain-predicate>
        实验谓词：{String(row.predicate.state)}
        {row.predicate.state === 'SYN_TEST_ONLY'
          ? ` · ${String(row.predicate.operator)} ${String(row.predicate.value)} ${String(row.predicate.unit)} · 规则 ${String(row.predicate.testRuleRef)}`
          : ''}
      </p>
      <p className="scc-warn">{row.note}</p>

      <h4>1 · 来源与条款定位</h4>
      {row.sourceClaimRefs.length === 0 ? (
        <p>本条没有候选来源断言（{row.sourceState}）。不补造来源。</p>
      ) : (
        row.sourceClaimRefs.map((claimId) => {
          const claim = claimsById.get(claimId);
          if (!claim) return <p key={claimId}>候选断言 {claimId} 不可达。</p>;
          const source = sourcesById.get(claim.sourceId);
          return (
            <article key={claimId} data-chain-claim={claimId}>
              <p>
                候选断言 {claim.claimId} · {claim.bindingNature} · 主张权威 {claim.claimedAuthority}
              </p>
              <p>{claim.paraphrase}</p>
              <p>
                来源 {claim.sourceId} · 定位 {claim.locatorRef}
                {source ? ` · ${source.title} · ${source.publisher}` : ''}
              </p>
              {source && (
                <a href={source.officialUrl} target="_blank" rel="noopener noreferrer">
                  官方页面 ↗
                </a>
              )}
              <p className="scc-warn">{claim.note}</p>
            </article>
          );
        })
      )}

      <h4>2 · 05 能力定义精确引用</h4>
      <p data-chain-mapping>
        映射 {row.mappingState} · 绑定 {ref.binding} · 原因 {ref.reason}
      </p>
      {ref.binding === 'EXACT' ? (
        <dl className="scc-knowledge__pin">
          <div>
            <dt>capabilityId</dt>
            <dd>{ref.capabilityId}</dd>
          </div>
          <div>
            <dt>definitionVersion</dt>
            <dd>{ref.resolvedDefinitionVersion}</dd>
          </div>
          <div>
            <dt>definitionDigest</dt>
            <dd>{ref.resolvedDefinitionDigest}</dd>
          </div>
          <div>
            <dt>definitionStatus</dt>
            <dd>{ref.resolvedDefinitionStatus}</dd>
          </div>
          <div>
            <dt>originLabel</dt>
            <dd>{ref.resolvedDefinitionOriginLabel}</dd>
          </div>
        </dl>
      ) : (
        <p>没有可核实的精确定义引用。不按名称、关键词或"最新版本"推定匹配。</p>
      )}

      <h4>3 · S4 评估证据</h4>
      <p data-chain-evidence>
        证据源 {evidence.availability} · 结论 {evidence.conclusion} · 绑定证据{' '}
        {evidence.boundEvidenceCount} 条
        {evidence.coverageComplete === undefined
          ? ''
          : ' · 覆盖完整 ' + String(evidence.coverageComplete)}
      </p>
      {evidence.detail && <p>{evidence.detail}</p>}
      <p className="scc-notice" data-chain-state>
        {row.evaluationState} — {row.evaluationReason}
      </p>
      {!compact && <p>评估结论不是可用性、就绪、承诺或派遣授权；本页不产生任何一项。</p>}
    </section>
  );
}
